import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpEvent, HttpHandler, HttpRequest, HttpResponse } from '@angular/common/http';
import { deepSort } from '@sg/utils/src';
import { Caching, SgCachingService } from './caching.service';

export type CachingOptions = Omit<Caching, 'url' | 'response' | 'timestamp'> & { forceRefresh?: boolean };

const optionsStore = {
  counter: 0,
  items: new Map<string, Partial<CachingOptions>>(),
  add(options: Partial<CachingOptions>): string {
    const id = String(this.counter++);
    this.items.set(id, options);
    return id;
  },
  get(id: string): Partial<CachingOptions> {
    return this.items.get(id);
  },
  delete(id: string): void {
    this.items.delete(id);
  },
};

export const CachingApplier = {
  apply(caching?: Partial<CachingOptions>) {
    const options: Partial<CachingOptions> = {
      saveMethod: 'sessionStorage',
      timeToLive: { minute: 1 },
      ...caching,
    };
    return { sgCachingOptions: optionsStore.add(options) };
  },
};

const X_SG_USER = 'x-sg-user';
const SG_CACHING_OPTIONS = 'sgCachingOptions';
@Injectable()
export class SgCachingInterceptor implements HttpInterceptor {
  constructor(private cachingService: SgCachingService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.headers.has(SG_CACHING_OPTIONS)) {
      return next.handle(req);
    }
    if (req.method !== 'GET' && req.method !== 'POST') {
      return next.handle(req);
    }

    const cachingOptions = this.getCachingOptions(req);

    if (cachingOptions.forceRefresh) {
      return next.handle(req);
    }

    const key = this.httpRequestToKey(req);
    const cached = this.cachingService.getCache(key);

    if (cached && isEqual(cached.timeToLive, cachingOptions.timeToLive)) {
      return of(new HttpResponse({ body: cached.response, status: 200 }));
    }

    return next.handle(req).pipe(
      tap((response) => {
        if (response instanceof HttpResponse) {
          this.setCache(req, response, cachingOptions);
        }
      })
    );
  }

  private getCachingOptions(req: HttpRequest<any>): Partial<CachingOptions> {
    const cachingStoreId = req.headers.get(SG_CACHING_OPTIONS);
    const cachingReceivedOptions = optionsStore.get(cachingStoreId);
    this.deleteSgCachingOptionsHeader(req);
    optionsStore.delete(cachingStoreId);
    return cachingReceivedOptions;
  }

  private httpRequestToKey(req: HttpRequest<any>): string {
    switch (req.method) {
      case 'GET':
        return this.getRequestToKey(req);
      case 'POST':
        return this.postRequestToKey(req);
      default:
        return null;
    }
  }

  private deleteSgCachingOptionsHeader(req: HttpRequest<any>): void {
    req.headers.delete(SG_CACHING_OPTIONS);
  }

  private getRequestToKey = (req: HttpRequest<any>): string => {
    return `${req.urlWithParams}${req.headers.get(X_SG_USER)}`;
  };

  private postRequestToKey = (req: HttpRequest<any>): string => {
    const sortedBody = deepSort(req.body);
    const bodyToString = JSON.stringify(sortedBody);
    return `${req.urlWithParams}${bodyToString}${req.headers.get(X_SG_USER)}`;
  };

  private setCache(req: HttpRequest<any>, response: HttpResponse<any>, cachingOptions: Partial<CachingOptions>) {
    let cache: Partial<Caching> = {
      request: req,
      response: response.body,
      saveMethod: cachingOptions.saveMethod,
      timeToLive: cachingOptions.timeToLive,
    };
    cache.url = this.httpRequestToKey(req);
    this.cachingService.setCache(cache);
  }
}
